import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, IndianRupee, CheckCircle2, Download } from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { loadRazorpay, inr } from '../lib/loadRazorpay';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Handles registration for both events and satsangs.
 * Props:
 *  - open, onClose
 *  - item: { id, title, pricing_mode, fee_amount }
 *  - kind: 'event' | 'satsang'
 */
export const RegistrationModal = ({ open, onClose, item, kind }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    dakshina_amount: '',
    pay_mode: 'full',
    part_amount: '',
  });
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(null); // {message}

  useEffect(() => {
    if (open) {
      setSuccess(null);
      setForm({ name: '', email: '', phone: '', city: '', dakshina_amount: '', pay_mode: 'full', part_amount: '' });
    }
  }, [open, item]);

  if (!open || !item) return null;

  const pricingMode = item.pricing_mode || 'free';
  const feeAmount = Number(item.fee_amount || 0);

  const allowPartial = pricingMode === 'fee' && item.allow_partial_payment;
  const minPartial = Number(item.min_partial_amount || 0) || 100;
  const isPart = allowPartial && form.pay_mode === 'part';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (processing) return;
    setProcessing(true);
    try {
      let payload = {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        city: form.city || null,
        amount: 0,
      };
      if (kind === 'event') payload.event_id = item.id;
      else if (kind === 'satsang') payload.satsang_id = item.id;
      else if (kind === 'course') payload.course_id = item.id;

      if (pricingMode === 'dakshina') {
        const paise = Math.round(parseFloat(form.dakshina_amount) * 100);
        if (!paise || paise <= 0) {
          toast.error('Please enter a Dakshina amount');
          setProcessing(false);
          return;
        }
        payload.amount = paise;
      }
      if (isPart) {
        const paise = Math.round(parseFloat(form.part_amount || '0') * 100);
        if (paise < minPartial || paise > feeAmount) {
          toast.error(`Enter an amount between ${inr(minPartial)} and ${inr(feeAmount)}`);
          setProcessing(false);
          return;
        }
        payload.pay_amount = paise;
      }

      const { data } = await axios.post(`${API_URL}/api/registrations/create-order`, payload);

      if (data.status === 'free') {
        setSuccess({
          message: `You're registered! A confirmation has been sent to ${form.email}.`,
        });
        setProcessing(false);
        return;
      }

      // Payment required
      const ok = await loadRazorpay();
      if (!ok) {
        toast.error('Could not load payment gateway');
        setProcessing(false);
        return;
      }

      const rzp = new window.Razorpay({
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: data.amount,
        currency: data.currency,
        name: 'Rishidev Foundation',
        description: `${data.payment_type === 'dakshina' ? 'Dakshina' : 'Fee'} · ${item.title}`,
        order_id: data.order_id,
        prefill: { name: form.name, email: form.email, contact: form.phone || '' },
        theme: { color: '#E67E22' },
        handler: async (response) => {
          try {
            const { data: v } = await axios.post(`${API_URL}/api/registrations/verify`, response);
            setSuccess({
              message: v.balance > 0
                ? `You paid ${inr(v.this_payment)} of ${inr(v.total)}. Balance due: ${inr(v.balance)}. A receipt has been emailed to ${form.email}.`
                : `Payment confirmed. A receipt has been emailed to ${form.email}.`,
              balanceLink: v.balance > 0 ? `/pay-balance/${v.registration_id}?email=${encodeURIComponent(form.email)}` : null,
            });
          } catch (err) {
            toast.error('Payment verification failed. Please contact us.');
          } finally {
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: () => setProcessing(false),
        },
      });
      rzp.open();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Registration failed';
      toast.error(typeof msg === 'string' ? msg : 'Registration failed');
      setProcessing(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="registration-modal"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 md:p-8">
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="text-xs uppercase tracking-widest text-saffron-600 font-semibold mb-1">
                  Register for {kind === 'event' ? 'Event' : kind === 'satsang' ? 'Satsang' : 'Course'}
                </div>
                <h3 className="text-xl md:text-2xl font-heading text-charcoal-800 leading-tight">
                  {item.title}
                </h3>
              </div>
              <button
                onClick={onClose}
                data-testid="registration-close"
                className="p-2 rounded-full hover:bg-charcoal-50 text-charcoal-500"
              >
                <X size={20} />
              </button>
            </div>

            {success ? (
              <div className="mt-6 text-center" data-testid="registration-success">
                <div className="w-16 h-16 rounded-full bg-saffron-100 text-saffron-600 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 size={28} />
                </div>
                <h4 className="text-lg font-heading text-charcoal-800 mb-2">
                  Namaste 🙏 You're registered!
                </h4>
                <p className="text-sm text-charcoal-600 mb-6" data-testid="registration-success-message">{success.message}</p>
                {success.balanceLink && (
                  <Link to={success.balanceLink} onClick={onClose} data-testid="registration-pay-balance-link" className="block text-sm text-saffron-700 underline mb-4">
                    Pay the remaining balance later from this link (also sent by email)
                  </Link>
                )}
                <button
                  onClick={onClose}
                  className="bg-saffron-600 text-white rounded-full px-6 py-2.5 font-body font-medium hover:bg-saffron-500 transition-colors"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="registration-form">
                {pricingMode !== 'free' && (
                  <div className="bg-saffron-50 border border-saffron-200 rounded-xl p-4 flex items-start gap-3">
                    <IndianRupee size={20} className="text-saffron-600 mt-0.5" />
                    <div className="text-sm text-charcoal-700">
                      {pricingMode === 'fee' ? (
                        <>
                          <strong>Registration Fee:</strong> ₹{(feeAmount / 100).toLocaleString('en-IN')}
                        </>
                      ) : (
                        <>
                          <strong>Dakshina (voluntary offering).</strong> Enter any amount that feels right — every contribution supports our mission.
                        </>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-body font-medium text-charcoal-700 mb-1.5">Full Name *</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    data-testid="reg-name"
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-body font-medium text-charcoal-700 mb-1.5">Email *</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    data-testid="reg-email"
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-body font-medium text-charcoal-700 mb-1.5">Phone</label>
                    <input
                      type="tel"
                      value={form.phone}
                      data-testid="reg-phone"
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-body font-medium text-charcoal-700 mb-1.5">City</label>
                    <input
                      type="text"
                      value={form.city}
                      data-testid="reg-city"
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
                    />
                  </div>
                </div>

                {allowPartial && (
                  <div className="space-y-3" data-testid="reg-pay-mode">
                    <div className="grid grid-cols-2 gap-2">
                      {[{ id: 'full', label: `Pay full ${inr(feeAmount)}` }, { id: 'part', label: 'Pay part now' }].map((o) => (
                        <button
                          key={o.id}
                          type="button"
                          onClick={() => setForm({ ...form, pay_mode: o.id })}
                          data-testid={`reg-pay-mode-${o.id}`}
                          className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${form.pay_mode === o.id ? 'border-saffron-500 bg-saffron-50 text-saffron-700' : 'border-charcoal-200 text-charcoal-600 hover:bg-charcoal-50'}`}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                    {isPart && (
                      <div>
                        <label className="block text-sm font-body font-medium text-charcoal-700 mb-1.5">Amount to pay now (₹) *</label>
                        <input
                          type="number"
                          required
                          min={minPartial / 100}
                          max={feeAmount / 100}
                          value={form.part_amount}
                          data-testid="reg-part-amount"
                          onChange={(e) => setForm({ ...form, part_amount: e.target.value })}
                          placeholder={`Minimum ${inr(minPartial)}`}
                          className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
                        />
                        <p className="text-xs text-charcoal-500 mt-1">Pay the rest any time later — you'll get a link by email.</p>
                      </div>
                    )}
                  </div>
                )}

                {pricingMode === 'dakshina' && (
                  <div>
                    <label className="block text-sm font-body font-medium text-charcoal-700 mb-1.5">
                      Dakshina Amount (₹) *
                    </label>
                    <input
                      type="number"
                      min="1"
                      required
                      value={form.dakshina_amount}
                      data-testid="reg-dakshina-amount"
                      onChange={(e) => setForm({ ...form, dakshina_amount: e.target.value })}
                      placeholder="Enter amount"
                      className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
                    />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={processing}
                  data-testid="reg-submit"
                  className="w-full bg-saffron-600 text-white rounded-full px-6 py-3.5 font-body font-medium hover:bg-saffron-500 transition-colors shadow disabled:opacity-50"
                >
                  {processing
                    ? 'Processing...'
                    : pricingMode === 'free'
                    ? 'Register Now'
                    : pricingMode === 'fee'
                    ? `Pay ${isPart && form.part_amount ? `₹${Number(form.part_amount).toLocaleString('en-IN')}` : inr(feeAmount)} & Register`
                    : 'Pay Dakshina & Register'}
                </button>
                <p className="text-[11px] text-charcoal-500 text-center">
                  A confirmation email will be sent{pricingMode !== 'free' ? ' with a receipt' : ''}. The joining link is shared closer to the event.
                </p>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
